import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { format, differenceInCalendarDays } from "date-fns";
import api, { apiErr, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { DateField, DateRangeField } from "../components/DateField";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Plus, Plane, Hotel, Users, FileText, Trash2, Loader2, Phone, Globe2, Calendar, Pencil, MapPin, Home as HomeIcon, Upload, Download, Star, ChevronRight, GripVertical, Heart, CheckCircle2, X } from "lucide-react";
import { tabsListClass, tabTriggerClass } from "../lib/tabStyles";
import TripMap from "../components/TripMap";
import { EmptyState } from "../components/EmptyState";
import PageHeader from "../components/PageHeader";
import TripStatsStrip from "../components/TripStatsStrip";
import { getTripStatus, orderCountries, getCountryProgress } from "../lib/tripStatus";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TYPES = [
  { key: "flight", label: "Flight", icon: Plane },
  { key: "lodging", label: "Lodging", icon: Hotel },
  { key: "meeting", label: "Meeting", icon: Users },
  { key: "note", label: "Note", icon: FileText },
];
const typeIcon = (t) => (TYPES.find((x) => x.key === t)?.icon || FileText);

const TYPE_FIELDS = {
  flight: { title: "Route (e.g. LAX → EVN)", detail: "Airline / notes", showLoc: false, showTime: true },
  lodging: { title: "Place name", detail: "Notes (nights, address)", showLoc: true, showTime: true },
  meeting: { title: "Meeting title", detail: "Purpose", showLoc: true, showTime: true },
  note: { title: "Note title", detail: "Details", showLoc: false, showTime: false },
};

export default function Itinerary() {
  const { user, config, refreshTrip, refreshConfig } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") === "documents" ? "documents" : "schedule");
  const [docView, setDocView] = useState(null);
  const [entries, setEntries] = useState([]);
  const [countries, setCountries] = useState([]);
  const [countryStats, setCountryStats] = useState({});
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  const [editBanner, setEditBanner] = useState(false);

  const load = async () => {
    try {
      const calls = [api.get("/itinerary/entries"), api.get("/itinerary/countries"), api.get("/itinerary/country-stats")];
      if (isAdmin) calls.push(api.get("/itinerary/documents"));
      const res = await Promise.all(calls);
      setEntries(res[0].data); setCountries(res[1].data); setCountryStats(res[2].data || {});
      if (isAdmin) setDocs(res[3].data);
      refreshTrip();
    } catch (err) { toast.error(apiErr(err)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const onCountryDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ordered = orderCountries(countries);
    const oldI = ordered.findIndex((c) => c.id === active.id);
    const newI = ordered.findIndex((c) => c.id === over.id);
    const next = arrayMove(ordered, oldI, newI);
    // Dates rule the sequence: block a drag that puts dated stops out of order.
    const dated = next.filter((c) => c.arrive_date);
    const conflict = dated.some((c, i) => i > 0 && c.arrive_date < dated[i - 1].arrive_date);
    if (conflict) {
      const moved = ordered[oldI];
      toast.warning("Dates decide the order", {
        description: `Stops are ordered by arrival date. To move ${moved?.name || "this stop"}, edit its arrival/departure dates.`,
        duration: 6000,
      });
      setCountries([...countries]); // snap back to date order
      return;
    }
    setCountries(next);
    try { await api.post("/itinerary/countries/reorder", { ids: next.map((c) => c.id) }); refreshTrip(); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  const tripStatus = useMemo(() => {
    const origin = config?.origin_lat != null && config?.origin_lng != null
      ? { lat: config.origin_lat, lng: config.origin_lng, country: config.origin_country, city: config.origin_city } : null;
    return getTripStatus(config, countries, origin);
  }, [config, countries]);
  const banner = useMemo(() => {
    const today = new Date();
    const start = config?.start_date ? new Date(config.start_date) : null;
    const end = config?.end_date ? new Date(config.end_date) : null;
    let day = null;
    if (start) {
      const d = differenceInCalendarDays(today, start);
      if (d < 0) day = d;
      else if (!end || differenceInCalendarDays(today, end) <= 0) day = d + 1;
    }
    return { day };
  }, [config]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin accent-text" /></div>;

  const sorted = [...entries].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const origin = config?.origin_lat != null && config?.origin_lng != null
    ? { lat: config.origin_lat, lng: config.origin_lng, country: config.origin_country, city: config.origin_city }
    : null;
  const orderedCountries = orderCountries(countries);
  const todayStr = format(new Date(), "yyyy-MM-dd");
  const todays = sorted.filter((e) => e.date === todayStr);
  const upcoming = sorted.filter((e) => !e.date || e.date > todayStr);
  const past = sorted.filter((e) => e.date && e.date < todayStr);
  const isLive = tripStatus.phase === "on_road";
  const autoNote = tripStatus.phase === "preparing"
    ? (tripStatus.atHome
        ? "The trip hasn't started yet — the team is at home base. Today's plans appear here once you're on the road."
        : "The road hasn't started yet. Once the trip begins, today's plans will appear here.")
    : tripStatus.phase === "complete"
      ? "The team has returned home. Relive the journey below."
      : "Follow the team's journey below.";

  const renderEntry = (e, muted) => {
    const Icon = typeIcon(e.entry_type);
    return (
      <div key={e.id} className={`rounded-2xl p-4 flex items-center gap-3 shadow-[0_4px_24px_rgba(44,44,62,0.04)] ${muted ? "bg-white/60" : "bg-white"}`} data-testid="itinerary-entry">
        <div className="h-11 w-11 rounded-xl bg-[var(--tenant-accent)]/12 flex items-center justify-center shrink-0"><Icon className="h-5 w-5 accent-text" /></div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#2C2C3E] truncate">{e.title}</div>
          {e.detail && <div className="text-sm text-muted-foreground truncate">{e.detail}</div>}
          {e.location && <div className="text-xs text-muted-foreground truncate">{e.location}</div>}
        </div>
        <div className="text-right shrink-0">
          {e.date && <div className="font-heading font-bold text-[var(--brand-navy)] flex items-center gap-1 justify-end"><Calendar className="h-3.5 w-3.5 accent-text" />{format(new Date(e.date), "MMM d")}</div>}
          {e.time && <div className="text-xs text-muted-foreground">{e.time}</div>}
          {isAdmin && <EntryDialog entry={e} onSaved={load} compact />}
        </div>
      </div>
    );
  };

  const sectionLabel = (t) => <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold pt-2">{t}</div>;

  return (
    <div>
      <PageHeader title="Trip" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className={tabsListClass(isAdmin ? 3 : 2)}>
          <TabsTrigger value="schedule" className={tabTriggerClass} data-testid="tab-schedule"><Calendar className="h-4 w-4" /> Schedule</TabsTrigger>
          <TabsTrigger value="countries" className={tabTriggerClass} data-testid="tab-countries"><Globe2 className="h-4 w-4" /> Countries</TabsTrigger>
          {isAdmin && <TabsTrigger value="documents" className={tabTriggerClass} data-testid="tab-documents"><FileText className="h-4 w-4" /> Documents</TabsTrigger>}
        </TabsList>

        <TabsContent value="schedule" className="space-y-3">
          <div className="relative rounded-2xl p-5 text-white shadow-[0_8px_32px_rgba(44,44,62,0.12)]" style={{ background: "linear-gradient(135deg,var(--brand-navy),var(--brand-navy-dark))" }} data-testid="trip-banner">
            {isAdmin && (
              <button data-testid="edit-banner-btn" onClick={() => setEditBanner(true)}
                className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors" title="Edit banner text">
                <Pencil className="h-4 w-4 text-white" />
              </button>
            )}
            <div className="text-xs uppercase tracking-widest font-semibold" style={{ color: "var(--tenant-accent-light)" }}>
              {tripStatus.phaseLabel.toUpperCase()}{banner.day !== null ? ` · Day ${banner.day}` : ""}
            </div>
            <div className="font-heading font-bold text-2xl mt-1 flex items-center gap-2 pr-8" data-testid="trip-banner-location">{tripStatus.atHome ? <HomeIcon className="h-5 w-5" /> : <MapPin className="h-5 w-5" />} {config?.banner_title || tripStatus.label || "Location TBD"}</div>
            <p className="text-sm opacity-80 mt-2">{config?.banner_note || autoNote}</p>
          </div>
          <BannerEditDialog open={editBanner} onClose={() => setEditBanner(false)} config={config} autoTitle={tripStatus.label} autoNote={autoNote} onSaved={refreshConfig} />

          {isAdmin && <EntryDialog onSaved={load} />}
          {sorted.length === 0 && (
            <EmptyState
              testid="schedule-empty"
              icon={Calendar}
              title="No plans on the schedule yet"
              description={isAdmin ? "Add flights, lodging and meetings above so everyone knows what's next." : "The team's flights, lodging and meetings will show up here."}
            />
          )}

          {isLive && (
            <div data-testid="today-plan-section">
              {sectionLabel("Today's plan")}
              {todays.length === 0 ? (
                <div className="rounded-2xl border border-dashed p-5 text-center text-sm text-muted-foreground">No plans scheduled for today — enjoy the journey.</div>
              ) : (
                <div className="space-y-3">
                  {todays.map((e) => (
                    <div key={e.id} className="rounded-2xl accent-border border-2 p-0.5">{renderEntry(e, false)}</div>
                  ))}
                </div>
              )}
            </div>
          )}

          {upcoming.length > 0 && (
            <>
              {sectionLabel(isLive ? "Later" : "Schedule")}
              <div className="space-y-3">{upcoming.map((e) => renderEntry(e, false))}</div>
            </>
          )}

          {past.length > 0 && (
            <div>
              <button data-testid="past-toggle" onClick={() => setShowPast(!showPast)} className="flex items-center gap-1 text-xs uppercase tracking-widest text-muted-foreground font-semibold pt-3 pb-1">
                <ChevronRight className={`h-3.5 w-3.5 transition-transform ${showPast ? "rotate-90" : ""}`} /> Earlier ({past.length})
              </button>
              {showPast && <div className="space-y-3 opacity-75">{past.map((e) => renderEntry(e, true))}</div>}
            </div>
          )}
        </TabsContent>

        <TabsContent value="countries" className="space-y-3">
          {countries.length > 0 && <TripStatsStrip config={config} countries={countries} entries={entries} isAdmin={isAdmin} onSaved={refreshConfig} />}
          {(origin || countries.some((c) => typeof c.lat === "number" && typeof c.lng === "number")) && (
            <div className="mb-1">
              <TripMap countries={countries} origin={origin} currentId={tripStatus.currentId} onSelectCountry={(id) => {
                const el = document.getElementById("country-" + id);
                if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.classList.add("ring-2", "ring-[var(--tenant-accent)]"); setTimeout(() => el.classList.remove("ring-2", "ring-[var(--tenant-accent)]"), 1600); }
              }} />
              <p className="text-[11px] text-muted-foreground text-center mt-1.5">Pinch or drag to explore · numbers show visit order</p>
            </div>
          )}
          {isAdmin && (
            <button data-testid="build-trip-btn" onClick={() => navigate("/build-trip")}
              className="w-full rounded-2xl p-4 text-left text-white shadow-[0_8px_32px_rgba(44,44,62,0.12)] flex items-center gap-3 hover:opacity-95 transition-opacity" style={{ background: "linear-gradient(135deg,var(--brand-navy),var(--brand-navy-dark))" }}>
              <MapPin className="h-6 w-6 accent-text shrink-0" />
              <div>
                <div className="font-heading font-bold text-lg">Re-map the trip route</div>
                <div className="text-xs opacity-80">Guided builder to rebuild all your countries & dates — use when plans change or a new country opens up.</div>
              </div>
            </button>
          )}
          {isAdmin && <CountryDialog onSaved={load} />}
          {countries.length === 0 && (
            <EmptyState
              testid="countries-empty"
              icon={Globe2}
              title="No stops mapped yet"
              description={isAdmin ? "Build your trip or add a stop above to plot your route on the map." : "The countries the team is visiting will appear here with a live route map."}
            />
          )}
          {isAdmin && countries.length > 1 && (
            <p className="text-[11px] text-muted-foreground flex items-center gap-1"><GripVertical className="h-3.5 w-3.5" /> Ordered by arrival date · drag to arrange stops without dates</p>
          )}
          {isAdmin ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onCountryDragEnd}>
              <SortableContext items={orderedCountries.map((c) => c.id)} strategy={verticalListSortingStrategy}>
                <div className="space-y-3">
                  {orderedCountries.map((c, idx) => (
                    <SortableCountryCard key={c.id} c={c} idx={idx} isAdmin={isAdmin} onSaved={load} currentId={tripStatus.currentId} stats={countryStats[(c.name || "").trim().toLowerCase()]} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="space-y-3">
              {orderedCountries.map((c, idx) => (
                <CountryCard key={c.id} c={c} idx={idx} isAdmin={isAdmin} onSaved={load} currentId={tripStatus.currentId} stats={countryStats[(c.name || "").trim().toLowerCase()]} />
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="documents" className="space-y-3">
          {isAdmin && <DocumentUpload onSaved={load} />}
          {docs.length === 0 && (
            <EmptyState
              testid="documents-empty"
              icon={FileText}
              title="No travel documents yet"
              description="Upload passports, visas, tickets and itineraries above to keep them handy for the whole team."
            />
          )}
          {docs.map((d) => (
            <div key={d.id} className="bg-white rounded-2xl p-4 flex items-center gap-3 shadow-[0_4px_24px_rgba(44,44,62,0.04)]" data-testid="document-card">
              <button data-testid="document-open" onClick={() => setDocView(d)} className="h-11 w-11 rounded-xl bg-[var(--brand-navy)]/10 flex items-center justify-center shrink-0 hover:bg-[var(--brand-navy)]/20 transition-colors"><FileText className="h-5 w-5 text-[var(--brand-navy)]" /></button>
              <button onClick={() => setDocView(d)} className="flex-1 min-w-0 text-left" data-testid="document-title-btn">
                <div className="font-semibold text-[#2C2C3E] truncate">{d.title}</div>
                {d.doc_type && <div className="text-xs text-muted-foreground">{d.doc_type}</div>}
                {d.note && <div className="text-xs text-muted-foreground truncate">{d.note}</div>}
              </button>
              {d.file_id && <a data-testid="document-download" href={fileUrl(`/api/files/${d.file_id}`)} target="_blank" rel="noreferrer" className="text-[var(--brand-navy)] hover:opacity-70"><Download className="h-5 w-5" /></a>}
              {isAdmin && <button data-testid="document-delete-btn" onClick={async () => { try { await api.delete(`/itinerary/documents/${d.id}`); load(); } catch (e) { toast.error(apiErr(e)); } }} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </TabsContent>
      </Tabs>
      {docView && <DocViewer doc={docView} onClose={() => setDocView(null)} />}
    </div>
  );
}

function DocViewer({ doc, onClose }) {
  const src = fileUrl(`/api/files/${doc.file_id}`);
  const isImage = /\.(png|jpe?g|gif|webp|heic|heif)$/i.test(doc.file_name || "") || (doc.content_type || "").startsWith("image/");
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-[#0d2340]" data-testid="doc-viewer" style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 shrink-0">
        <button data-testid="doc-viewer-close" onClick={onClose} className="h-10 w-10 rounded-full flex items-center justify-center text-white hover:bg-white/15 transition-colors shrink-0">
          <X className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-white truncate">{doc.title}</div>
          {doc.doc_type && <div className="text-xs text-white/60 truncate">{doc.doc_type}</div>}
        </div>
        <a data-testid="doc-viewer-download" href={src} target="_blank" rel="noreferrer" download
          className="h-10 px-4 rounded-full flex items-center gap-1.5 text-sm font-semibold bg-white/15 text-white hover:bg-white/25 transition-colors shrink-0">
          <Download className="h-4 w-4" /> Download
        </a>
      </div>
      <div className="flex-1 min-h-0 bg-white/5 overflow-auto flex items-start justify-center">
        {doc.file_id ? (
          isImage
            ? <img src={src} alt={doc.title} className="max-w-full h-auto" data-testid="doc-viewer-image" />
            : <iframe src={src} title={doc.title} className="w-full h-full border-0" data-testid="doc-viewer-frame" />
        ) : (
          <div className="text-white/70 text-sm p-8 text-center">No file attached to this document.</div>
        )}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-xl bg-muted/50 p-2.5">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-[#2C2C3E] font-medium break-words">{value}</div>
    </div>
  );
}

function EntryDialog({ entry, onSaved, compact }) {
  const [open, setOpen] = useState(false);
  const blank = { entry_type: "flight", title: "", detail: "", location: "", country: "", date: "", time: "" };
  const [f, setF] = useState(entry ? { ...blank, ...entry } : blank);
  const cfg = TYPE_FIELDS[f.entry_type] || TYPE_FIELDS.flight;

  const openIt = () => { setF(entry ? { ...blank, ...entry } : blank); setOpen(true); };
  const save = async () => {
    if (!f.title) return toast.error("Title required");
    const payload = { entry_type: f.entry_type, title: f.title, detail: f.detail, location: f.location, country: f.country, date: f.date || null, time: f.time || null };
    try {
      if (entry) await api.put(`/itinerary/entries/${entry.id}`, payload);
      else await api.post("/itinerary/entries", payload);
      setOpen(false); onSaved(); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => { try { await api.delete(`/itinerary/entries/${entry.id}`); setOpen(false); onSaved(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <>
      {compact ? (
        <button data-testid="entry-edit-btn" onClick={openIt} className="text-muted-foreground hover:text-[var(--brand-navy)] mt-1"><Pencil className="h-3.5 w-3.5" /></button>
      ) : (
        <Button data-testid="add-entry-btn" onClick={openIt} variant="outline" className="rounded-full w-full border-dashed"><Plus className="h-4 w-4 mr-1" /> Add entry</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">{entry ? "Edit entry" : "Itinerary entry"}</DialogTitle><DialogDescription className="sr-only">Itinerary entry form</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-4 gap-2">
              {TYPES.map((t) => {
                const Icon = t.icon;
                return (
                  <button key={t.key} data-testid={`entry-type-${t.key}`} onClick={() => setF({ ...f, entry_type: t.key })}
                    className={`flex flex-col items-center gap-1 rounded-xl py-2 text-[11px] border ${f.entry_type === t.key ? "accent-border accent-text bg-[var(--tenant-accent)]/10" : "border-border text-muted-foreground"}`}>
                    <Icon className="h-4 w-4" /> {t.label}
                  </button>
                );
              })}
            </div>
            <div><Label className="text-sm">{cfg.title}</Label><Input data-testid="entry-title-input" className="rounded-xl h-11 mt-1" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
            {f.entry_type === "note" ? (
              <div><Label className="text-sm">{cfg.detail}</Label><Textarea data-testid="entry-detail-input" className="rounded-xl mt-1" value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} /></div>
            ) : (
              <div><Label className="text-sm">{cfg.detail}</Label><Input data-testid="entry-detail-input" className="rounded-xl h-11 mt-1" value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} /></div>
            )}
            {cfg.showLoc && (
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-sm">Location</Label><Input data-testid="entry-location-input" className="rounded-xl h-11 mt-1" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
                <div><Label className="text-sm">Country</Label><Input data-testid="entry-country-input" className="rounded-xl h-11 mt-1" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
              </div>
            )}
            <div className={cfg.showTime ? "grid grid-cols-2 gap-2" : ""}>
              <div><Label className="text-sm">Date</Label><DateField value={f.date || ""} onChange={(v) => setF({ ...f, date: v })} testid="entry-date-input" placeholder="Pick a date" /></div>
              {cfg.showTime && <div><Label className="text-sm">Time</Label><Input data-testid="entry-time-input" type="time" className="rounded-xl h-11 mt-1" value={f.time || ""} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>}
            </div>
            <div className="flex gap-2">
              <Button data-testid="entry-save-btn" onClick={save} className="flex-1 rounded-full accent-bg text-white h-11 hover:opacity-90">Save</Button>
              {entry && <Button data-testid="entry-delete-btn" onClick={del} variant="outline" className="rounded-full h-11 border-destructive text-destructive"><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function CountryDialog({ country, onSaved, compact }) {
  const [open, setOpen] = useState(false);
  const blank = { name: "", city: "", cities: [], currency: "", language: "", embassy: "", emergency: "", highlight: "", notes: "", is_current: false, arrive_date: "", depart_date: "", prayer_points: [], people: [] };
  const merge = (c) => {
    const base = { ...blank, ...(c || {}) };
    let cities = Array.isArray(base.cities) ? base.cities.map((x) => ({ name: x.name || "" })) : [];
    if (!cities.length && base.city) cities = String(base.city).split(",").map((s) => ({ name: s.trim() })).filter((x) => x.name);
    return {
      ...Object.fromEntries(Object.entries(base).map(([k, v]) => [k, (k === "prayer_points" || k === "people" || k === "cities") ? v : (v ?? "")])),
      prayer_points: Array.isArray(base.prayer_points) ? base.prayer_points : [],
      people: Array.isArray(base.people) ? base.people : [],
      cities,
    };
  };
  const [f, setF] = useState(merge(country));
  const openIt = () => { setF(merge(country)); setOpen(true); };
  const setPeople = (people) => setF((p) => ({ ...p, people }));
  const setCity = (i, name) => setF((p) => ({ ...p, cities: p.cities.map((c, idx) => (idx === i ? { name } : c)) }));
  const addCity = () => setF((p) => ({ ...p, cities: [...p.cities, { name: "" }] }));
  const removeCity = (i) => setF((p) => ({ ...p, cities: p.cities.filter((_, idx) => idx !== i) }));
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    const cities = (f.cities || []).map((c) => ({ name: (c.name || "").trim() })).filter((c) => c.name);
    const payload = {
      name: f.name, city: null, cities, currency: f.currency, language: f.language,
      embassy: f.embassy, emergency: f.emergency, highlight: f.highlight, notes: f.notes,
      is_current: !!f.is_current, arrive_date: f.arrive_date || null, depart_date: f.depart_date || null,
      prayer_points: (f.prayer_points || []).map((s) => (s || "").trim()).filter(Boolean),
      people: (f.people || []).filter((p) => p && (p.name || "").trim()).map((p) => ({ name: p.name.trim(), role: (p.role || "").trim() })),
    };
    try {
      if (country) await api.put(`/itinerary/countries/${country.id}`, payload);
      else await api.post("/itinerary/countries", payload);
      setOpen(false); onSaved(); toast.success("Saved");
    } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async () => { try { await api.delete(`/itinerary/countries/${country.id}`); setOpen(false); onSaved(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <>
      {compact ? (
        <button data-testid="country-edit-btn" onClick={openIt} className="text-muted-foreground hover:text-[var(--brand-navy)]"><Pencil className="h-4 w-4" /></button>
      ) : (
        <Button data-testid="add-country-btn" onClick={openIt} variant="outline" className="rounded-full w-full border-dashed"><Plus className="h-4 w-4 mr-1" /> Add stop</Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">{country ? "Edit stop" : "Add a stop"}</DialogTitle><DialogDescription className="sr-only">Country info form</DialogDescription></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-muted-foreground">Country</Label><Input data-testid="country-name-input" placeholder="Country" className="rounded-xl h-11 mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
              <div className="flex items-end"><p className="text-[11px] text-muted-foreground pb-2">Add the cities you'll visit below — each becomes its own pin on the map.</p></div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Cities (optional)</Label>
              <div className="space-y-2 mt-1" data-testid="country-cities-list">
                {f.cities.map((c, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input data-testid={`country-city-input-${i}`} placeholder={`City ${i + 1}`} className="rounded-xl h-10 flex-1" value={c.name} onChange={(e) => setCity(i, e.target.value)} />
                    <button type="button" data-testid={`country-city-remove-${i}`} onClick={() => removeCity(i)} className="h-9 w-9 shrink-0 rounded-xl border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/40"><X className="h-4 w-4" /></button>
                  </div>
                ))}
                <button type="button" data-testid="country-city-add" onClick={addCity} className="flex items-center gap-1.5 text-sm font-medium accent-text hover:underline"><Plus className="h-4 w-4" /> Add a city</button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Arrive → Depart</Label>
              <DateRangeField from={f.arrive_date || ""} to={f.depart_date || ""}
                onChange={({ from, to }) => setF({ ...f, arrive_date: from, depart_date: to })} testid="country-daterange-input" />
            </div>
            <div>
              <Label className="text-sm flex items-center gap-2"><Star className="h-4 w-4 accent-text" /> Why we're here</Label>
              <Textarea data-testid="country-highlight-input" className="rounded-xl mt-1" value={f.highlight} onChange={(e) => setF({ ...f, highlight: e.target.value })} placeholder="One sentence of purpose — shared with partners" />
            </div>
            <div>
              <Label className="text-sm flex items-center gap-2"><Heart className="h-4 w-4 accent-text" /> What we're praying for</Label>
              <Textarea data-testid="country-prayer-input" className="rounded-xl mt-1" rows={4}
                value={(f.prayer_points || []).join("\n")}
                onChange={(e) => setF({ ...f, prayer_points: e.target.value.split("\n") })}
                placeholder={"One prayer point per line, e.g.\nNew friendships\nLocal church connections\nWisdom"} />
              <p className="text-[11px] text-muted-foreground mt-1">One prayer point per line.</p>
            </div>
            <div>
              <Label className="text-sm flex items-center gap-2"><Users className="h-4 w-4 accent-text" /> Meet the people</Label>
              <div className="space-y-2 mt-1">
                {(f.people || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input data-testid={`person-name-${i}`} placeholder="Name (e.g. Pastor Alex)" className="rounded-xl h-11 flex-1" value={p.name || ""} onChange={(e) => setPeople(f.people.map((x, idx) => idx === i ? { ...x, name: e.target.value } : x))} />
                    <Input data-testid={`person-role-${i}`} placeholder="Role" className="rounded-xl h-11 w-32" value={p.role || ""} onChange={(e) => setPeople(f.people.map((x, idx) => idx === i ? { ...x, role: e.target.value } : x))} />
                    <button data-testid={`person-remove-${i}`} onClick={() => setPeople(f.people.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
                <Button data-testid="person-add-btn" type="button" variant="outline" className="rounded-full w-full border-dashed" onClick={() => setPeople([...(f.people || []), { name: "", role: "" }])}><Plus className="h-4 w-4 mr-1" /> Add a person</Button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="country-currency-input" placeholder="Currency" className="rounded-xl h-11" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })} />
              <Input data-testid="country-language-input" placeholder="Language" className="rounded-xl h-11" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })} />
            </div>
            <div className="rounded-xl border p-3 space-y-2">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Leaders only (hidden from partners)</div>
              <Input data-testid="country-embassy-input" placeholder="Embassy contact" className="rounded-xl h-11" value={f.embassy} onChange={(e) => setF({ ...f, embassy: e.target.value })} />
              <Input data-testid="country-emergency-input" placeholder="Emergency number" className="rounded-xl h-11" value={f.emergency} onChange={(e) => setF({ ...f, emergency: e.target.value })} />
            </div>
            <Textarea data-testid="country-notes-input" placeholder="Notes (packing, tips)" className="rounded-xl" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
            <div className="flex items-center justify-between"><Label className="text-sm">Currently here</Label><Switch data-testid="country-current-switch" checked={!!f.is_current} onCheckedChange={(v) => setF({ ...f, is_current: v })} /></div>
            <div className="flex gap-2">
              <Button data-testid="country-save-btn" onClick={save} className="flex-1 rounded-full accent-bg text-white h-11 hover:opacity-90">Save</Button>
              {country && <Button data-testid="country-delete-btn" onClick={del} variant="outline" className="rounded-full h-11 border-destructive text-destructive"><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function personInitials(name) {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}
function countryShort(name) {
  return (name || "").split(",").pop().trim();
}

function CountryCard({ c, idx, isAdmin, onSaved, currentId, stats, dragHandleProps, dragRef, style }) {
  const prog = getCountryProgress(c, currentId);
  const isCur = prog.phase === "current";
  const isPast = prog.phase === "past";
  const s = stats || { dispatches: 0, prayers: 0, photos: 0, videos: 0 };
  const points = Array.isArray(c.prayer_points) ? c.prayer_points.filter(Boolean) : [];
  const people = Array.isArray(c.people) ? c.people.filter((p) => p && p.name) : [];
  return (
    <div id={"country-" + c.id} ref={dragRef} style={style} className={`rounded-2xl p-5 shadow-[0_4px_24px_rgba(44,44,62,0.04)] transition-all ${isPast ? "bg-muted/40" : "bg-white"} ${isCur ? "ring-2 ring-[var(--tenant-accent)]/50" : ""}`} data-testid="country-card">
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-heading font-bold text-lg text-[var(--brand-navy)] flex items-center gap-2 min-w-0">
          {dragHandleProps && <button type="button" data-testid={`country-drag-${idx}`} className="touch-none cursor-grab text-muted-foreground hover:text-[var(--brand-navy)] shrink-0" {...dragHandleProps}><GripVertical className="h-5 w-5" /></button>}
          {isPast
            ? <span className="shrink-0"><CheckCircle2 className="h-6 w-6 accent-text" /></span>
            : <span className="h-6 w-6 rounded-full bg-[var(--brand-navy)]/10 text-[var(--brand-navy)] text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>}
          <span className="truncate">{c.name}</span>
        </h3>
        {isAdmin && <CountryDialog country={c} onSaved={onSaved} compact />}
      </div>

      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        {isCur && <span data-testid="country-status-current" className="text-[10px] uppercase tracking-wide accent-text bg-[var(--tenant-accent)]/12 px-2 py-0.5 rounded-full">Currently here</span>}
        {isPast && <span data-testid="country-status-completed" className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-2 py-0.5 rounded-full">✓ Completed</span>}
        {prog.phase === "upcoming" && <span className="text-[10px] uppercase tracking-wide text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Upcoming</span>}
        {c.city && <span className="text-sm text-muted-foreground flex items-center gap-1"><MapPin className="h-3.5 w-3.5 accent-text" />{c.city}</span>}
      </div>
      {(c.arrive_date || c.depart_date) && (
        <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Calendar className="h-3 w-3 accent-text" />{[c.arrive_date, c.depart_date].filter(Boolean).join(" → ")}</div>
      )}

      {c.highlight && (
        <div className="mt-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold">Why we're here</div>
          <p className="text-sm text-[#2C2C3E]/90 mt-0.5">{c.highlight}</p>
        </div>
      )}

      {prog.daysTotal && !isPast && (
        <div className="mt-3">
          <div className="flex justify-between text-[11px] text-muted-foreground mb-1">
            <span>{isCur ? `Day ${prog.daysDone} of ${prog.daysTotal}` : `${prog.daysTotal} days planned`}</span>
            {isCur && prog.weekTotal > 1 && <span>Week {prog.weekNow} of {prog.weekTotal}</span>}
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full accent-bg transition-all" style={{ width: `${isCur ? prog.pct : 0}%` }} data-testid="country-progress-bar" /></div>
        </div>
      )}

      {points.length > 0 && (
        <div className="mt-3" data-testid="country-prayer-points">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><Heart className="h-3.5 w-3.5 accent-text" /> What we're praying for</div>
          <ul className="mt-1 space-y-0.5">{points.map((p, i) => (<li key={i} className="text-sm text-[#2C2C3E]/90 flex gap-2"><span className="accent-text">•</span> <span>{p}</span></li>))}</ul>
        </div>
      )}

      {people.length > 0 && (
        <div className="mt-3" data-testid="country-people">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1"><Users className="h-3.5 w-3.5 accent-text" /> Meet the people</div>
          <div className="mt-1.5 flex flex-wrap gap-2">{people.map((p, i) => (
            <div key={i} className="flex items-center gap-2 bg-muted/60 rounded-full pl-1 pr-3 py-1">
              <span className="h-6 w-6 rounded-full bg-[var(--brand-navy)]/10 text-[var(--brand-navy)] text-[10px] font-bold flex items-center justify-center">{personInitials(p.name)}</span>
              <span className="text-xs"><span className="font-medium text-[#2C2C3E]">{p.name}</span>{p.role ? ` · ${p.role}` : ""}</span>
            </div>))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
        {c.currency && <Info label="Currency" value={c.currency} />}
        {c.language && <Info label="Language" value={c.language} />}
        {isAdmin && c.embassy && <Info label="Embassy" value={c.embassy} />}
        {isAdmin && c.emergency && <Info label="Emergency" value={c.emergency} />}
      </div>
      {c.notes && <p className="text-sm text-[#2C2C3E]/70 mt-3">{c.notes}</p>}

      <div className="mt-3 pt-3 border-t flex items-center gap-x-4 gap-y-1 text-xs text-muted-foreground flex-wrap" data-testid="country-memories">
        <span className="font-semibold text-[#2C2C3E]/70">{isPast ? "Memories" : `Memories from ${countryShort(c.name)}`}</span>
        <span>{s.dispatches} dispatches</span>
        <span>{s.prayers} prayer updates</span>
        <span>{s.photos} photos</span>
        {s.videos > 0 && <span>{s.videos} videos</span>}
      </div>
    </div>
  );
}

function SortableCountryCard(props) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.c.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, zIndex: isDragging ? 20 : undefined, position: "relative" };
  return <CountryCard {...props} dragRef={setNodeRef} style={style} dragHandleProps={{ ...attributes, ...listeners }} />;
}

function DocumentUpload({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", doc_type: "", note: "", file_id: null });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("purpose", "document");
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, file_id: data.file_id, title: p.title || file.name }));
      toast.success("File uploaded");
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); }
  };
  const save = async () => {
    if (!f.title) return toast.error("Title required");
    setSaving(true);
    try { await api.post("/itinerary/documents", f); setOpen(false); setF({ title: "", doc_type: "", note: "", file_id: null }); onSaved(); toast.success("Document added"); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button data-testid="add-document-btn" onClick={() => setOpen(true)} variant="outline" className="rounded-full w-full border-dashed"><Plus className="h-4 w-4 mr-1" /> Add document</Button>
      <DialogContent className="rounded-3xl max-w-md">
        <DialogHeader><DialogTitle className="font-heading text-[var(--brand-navy)]">Travel document</DialogTitle><DialogDescription className="sr-only">Upload a travel document</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm rounded-xl border border-dashed p-3 hover:bg-muted/40 transition-colors">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 accent-text" />}
            {f.file_id ? "File attached ✓" : "Upload file (PDF/image)"}
            <input type="file" className="hidden" onChange={onFile} data-testid="document-file-input" />
          </label>
          <Input data-testid="document-title-input" placeholder="Title (e.g. Flight ticket)" className="rounded-xl h-11" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
          <Input data-testid="document-type-input" placeholder="Type (passport, visa, ticket)" className="rounded-xl h-11" value={f.doc_type} onChange={(e) => setF({ ...f, doc_type: e.target.value })} />
          <Textarea data-testid="document-note-input" placeholder="Note (optional)" className="rounded-xl" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
          <Button data-testid="document-save-btn" disabled={saving} onClick={save} className="w-full rounded-full accent-bg text-white h-11 hover:opacity-90">{saving ? "Saving…" : "Add document"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function BannerEditDialog({ open, onClose, config, autoTitle, autoNote, onSaved }) {
  const [title, setTitle] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setTitle(config?.banner_title || ""); setNote(config?.banner_note || ""); }
  }, [open, config]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/config", { banner_title: title.trim(), banner_note: note.trim() });
      await onSaved();
      toast.success("Trip banner updated");
      onClose();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-3xl max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit trip banner</DialogTitle>
          <DialogDescription className="text-xs">Customize the headline and message at the top of the Trip page. Leave a field blank to use the automatic text.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <div>
            <Label className="text-sm">Headline</Label>
            <Input data-testid="banner-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={autoTitle || "e.g. Daytona Beach, United States"} className="rounded-xl h-11 mt-1" />
          </div>
          <div>
            <Label className="text-sm">Message</Label>
            <Textarea data-testid="banner-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder={autoNote} rows={3} className="rounded-xl mt-1" />
          </div>
          <Button data-testid="banner-save-btn" disabled={busy} onClick={save} className="w-full rounded-full h-11 accent-bg text-white hover:opacity-90">{busy ? "Saving…" : "Save banner"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
